import { useState } from 'react';
import type { PersonId } from '@shared/types.ts';
import { FEATURE_KEYS, FEATURES, type FeatureKey, isFinished, isLongLived, OWNER, type Stage, stageOf } from '@shared/features.ts';
import { getStore, useAppState, useUi } from '../../data/store.ts';
import { Icon } from '../../components/Icon.tsx';
import { back } from '../../lib/router.tsx';

const STAGES: Array<{ stage: Stage; label: string }> = [
  { stage: 'off', label: 'Off' },
  { stage: 'me', label: 'Just me' },
  { stage: 'everyone', label: 'Everyone' },
];

/** Michael only: release unfinished features to Natalie one at a time. */
export function DeveloperScreen() {
  const store = getStore();
  const ui = useUi();
  const people = useAppState((s) => s.people);
  const other = people.find((p) => p.id !== OWNER);
  const otherName = other?.name ?? 'Natalie';
  const features = useAppState((s) => s.features);
  const stageIs = (k: FeatureKey) => (isFinished(features.find((f) => f.id === k)) ? 'finished' : stageOf(features, k));
  const progress = FEATURE_KEYS.filter((k) => stageIs(k) === 'off' || stageIs(k) === 'me');
  const live = FEATURE_KEYS.filter((k) => stageIs(k) === 'everyone');
  const finished = FEATURE_KEYS.filter((k) => stageIs(k) === 'finished');
  return (
    <div className="screen" style={{ gap: 14 }}>
      <button type="button" className="back-link" onClick={() => back('/settings')}>
        <Icon name="chevron-left" size={18} /> Settings
      </button>
      <div className="stack" style={{ gap: 4 }}>
        <h1 className="h1 h1-sm">Feature switches</h1>
        <span className="muted" style={{ fontSize: 14 }}>
          Unfinished features stay hidden from {otherName} until you pick Everyone. Her phone picks up a change the next time it syncs.
        </span>
      </div>

      <section className="card stack">
        <label className="row" style={{ gap: 10 }}>
          <input
            type="checkbox"
            className="checkbox"
            checked={!!ui.viewAs}
            onChange={(e) => store.setViewAs(e.target.checked ? (other?.id as PersonId) : undefined)}
          />
          <span style={{ fontWeight: 600 }}>See what {otherName} sees</span>
        </label>
        <span className="small muted">On this phone only. Hides everything that isn't on Everyone, until you turn this off.</span>
      </section>

      {progress.map((key) => (
        <FeatureCard key={key} id={key} otherName={otherName} />
      ))}
      <Fold title="On for everyone" keys={live} otherName={otherName} />
      <Fold title="Waiting for cleanup" keys={finished} otherName={otherName} />
    </div>
  );
}

/** A collapsed group of switches; hidden when empty. */
function Fold({ title, keys, otherName }: { title: string; keys: FeatureKey[]; otherName: string }) {
  const [open, setOpen] = useState(false);
  if (!keys.length) return null;
  return (
    <>
      <button type="button" className="btn btn-soft" aria-expanded={open} onClick={() => setOpen(!open)}>
        {title} ({keys.length}) {open ? '▴' : '▾'}
      </button>
      {open && keys.map((key) => <FeatureCard key={key} id={key} otherName={otherName} />)}
    </>
  );
}

function FeatureCard({ id, otherName }: { id: FeatureKey; otherName: string }) {
  const store = getStore();
  const features = useAppState((s) => s.features);
  const row = features.find((f) => f.id === id);
  const stage = stageOf(features, id);
  const labelId = `feature-${id}`;
  return (
    <section className="card stack" aria-labelledby={labelId}>
      <div className="stack" style={{ gap: 2 }}>
        <span id={labelId} style={{ fontWeight: 600 }}>
          {FEATURES[id].label}
        </span>
        <span className="small muted">{FEATURES[id].detail}</span>
      </div>
      <div className="segmented" role="group" aria-labelledby={labelId}>
        {STAGES.map((s) => (
          <button key={s.stage} type="button" className={stage === s.stage ? 'on' : ''} aria-pressed={stage === s.stage} onClick={() => store.setFeatureStage(id, s.stage)}>
            {s.label}
          </button>
        ))}
      </div>
      {stage === 'off' && <span className="small muted">Hidden from both of you.</span>}
      {stage === 'me' && <span className="small muted">Only you see it.</span>}
      {stage === 'everyone' &&
        (isFinished(row) ? (
          <div className="row" style={{ gap: 10, justifyContent: 'space-between' }}>
            <span className="small muted">Rollout finished. Stays on until the cleanup removes the switch.</span>
            <button type="button" className="btn btn-soft" onClick={() => store.setFeatureFinished(id, false)}>
              Undo
            </button>
          </div>
        ) : (
          <div className="row" style={{ gap: 10, justifyContent: 'space-between' }}>
            <span className="small muted">
              {otherName} has it{row?.liveSince ? ` since ${new Date(row.liveSince).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}.
              {isLongLived(id) ? ' A long-lived switch: finish it only if it should go.' : ''}
            </span>
            <button type="button" className="btn btn-soft" onClick={() => store.setFeatureFinished(id, true)}>
              Finish rollout
            </button>
          </div>
        ))}
    </section>
  );
}
